// src/components/NoteBody.tsx
import React from "react";

/**
 * Normalize pasted note text without collapsing user spacing.
 */
function normalizeNoteText(input: string | null | undefined): string {
  if (!input) return "";
  return input
    .normalize("NFKC")
    .replace(/\uFFFD/g, "")
    .replace(/\u0095/g, "•")
    .replace(/[•\u2022]+/g, "•")
    .replace(/[\u2012\u2013\u2014\u2015]/g, "-")
    .replace(/\u00A0/g, " ")
    .replace(/[\u200B\u200C\u200D\u200E\u200F\u2060\uFEFF]/g, "")
    .replace(/\r\n?/g, "\n")
    .trimEnd();
}

/** Inline parser (unchanged from your current) */
function parseInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let i = 0;

  const isStandalone = (s: string, at: number, token: string) => {
    const ch = token[token.length - 1];
    const leftSame = at - 1 >= 0 && s[at - 1] === ch;
    const rightSame = at + token.length < s.length && s[at + token.length] === ch;
    return !leftSame && !rightSame;
  };
  const findStandalone = (s: string, token: string, from: number) => {
    let pos = s.indexOf(token, from);
    while (pos !== -1) {
      if (isStandalone(s, pos, token)) return pos;
      pos = s.indexOf(token, pos + 1);
    }
    return -1;
  };

  while (i < text.length) {
    const startI = i;
    const idxTriple = text.indexOf("***", i);
    const idxHL = text.indexOf("==", i);
    const idxBold = text.indexOf("**", i);
    const idxIt = text.indexOf("*", i);

    type K = "tri" | "hl" | "b" | "i";
    const cands: Array<[number, K]> = [];
    if (idxTriple >= 0) cands.push([idxTriple, "tri"]);
    if (idxHL >= 0) cands.push([idxHL, "hl"]);
    if (idxBold >= 0) cands.push([idxBold, "b"]);
    if (idxIt >= 0) cands.push([idxIt, "i"]);

    if (cands.length === 0) { out.push(text.slice(i)); break; }

    cands.sort((a, b) => {
      if (a[0] !== b[0]) return a[0] - b[0];
      const order: Record<K, number> = { tri: 0, hl: 1, b: 2, i: 3 };
      return order[a[1]] - order[b[1]];
    });

    const nextIdx = cands[0][0];
    const kind = cands[0][1];

    if (nextIdx > i) { out.push(text.slice(i, nextIdx)); i = nextIdx; }

    if (kind === "tri" && text.slice(i, i + 3) === "***") {
      if (!isStandalone(text, i, "***")) { out.push(text[i]); i += 1; }
      else {
        const close = findStandalone(text, "***", i + 3);
        if (close !== -1 && close > i + 3) {
          out.push(<strong key={`${i}-tri`}><em>{parseInline(text.slice(i + 3, close))}</em></strong>);
          i = close + 3;
        } else { out.push("***"); i += 3; }
      }
    } else if (kind === "hl" && text.slice(i, i + 2) === "==") {
      const close = text.indexOf("==", i + 2);
      if (close !== -1 && close > i + 2) {
        out.push(<mark key={`${i}-hl`}>{parseInline(text.slice(i + 2, close))}</mark>);
        i = close + 2;
      } else { out.push("=="); i += 2; }
    } else if (kind === "b" && text.slice(i, i + 2) === "**") {
      if (text.slice(i, i + 3) === "***" && text.slice(i, i + 4) !== "****") { i += 1; }
      else if (!isStandalone(text, i, "**")) { out.push(text[i]); i += 1; }
      else {
        let close = text.indexOf("**", i + 2);
        const isFourRunClosingPair = (s: string, at: number) => at - 2 >= 0 && s.slice(at - 2, at + 2) === "****";
        while (close !== -1 && !(isStandalone(text, close, "**") || isFourRunClosingPair(text, close))) {
          close = text.indexOf("**", close + 1);
        }
        if (close !== -1 && close > i + 2) {
          out.push(<strong key={`${i}-b`}>{parseInline(text.slice(i + 2, close))}</strong>);
          i = close + 2;
        } else { out.push("*"); i += 1; }
      }
    } else if (kind === "i" && text[i] === "*") {
      if (text.slice(i, i + 2) === "**") { i += 1; }
      else if (text[i + 1] === " ") { out.push("*"); i += 1; }
      else if (!isStandalone(text, i, "*")) { out.push("*"); i += 1; }
      else {
        let close = text.indexOf("*", i + 1);
        while (close !== -1) {
          const closesPair = isStandalone(text, close, "*");
          const becomesBold = text[close - 1] === "*" || text[close + 1] === "*";
          if (closesPair && !becomesBold) break;
          close = text.indexOf("*", close + 1);
        }
        if (close !== -1 && close > i + 1) {
          out.push(<em key={`${i}-i`}>{parseInline(text.slice(i + 1, close))}</em>);
          i = close + 1;
        } else { out.push("*"); i += 1; }
      }
    } else { out.push(text[i]); i += 1; }

    if (i === startI) { out.push(text[i]); i += 1; }
  }

  return out;
}

// --- NESTED LIST LOGIC ---

type BulletNode = {
  content: string;        // marker stripped out
  children: BulletNode[];
  indent: number;         // absolute leading-space count
  noMarker?: boolean;     // plain-text line rendered as list item without a bullet
  extraSpaces?: number;   // visual indent for noMarker lines
  spacer?: boolean;       // blank-line spacer
};

type TreeResult = { lead: string | null; tree: BulletNode[] };

const INDENT_PER_LEVEL = 2;

function buildBulletTree(text: string): TreeResult {
  const lines = text.split("\n");
  const tree: BulletNode[] = [];
  const leadParts: string[] = [];
  const stack: BulletNode[] = []; // bullet parents only
  let currentLevel: BulletNode[] = tree;
  let seenAnyBullet = false;

  const pushBullet = (indent: number, content: string) => {
    while (stack.length > 0 && indent <= stack[stack.length - 1].indent) {
      stack.pop();
    }
    currentLevel = stack.length > 0 ? stack[stack.length - 1].children : tree;
    const node: BulletNode = { content, children: [], indent };
    currentLevel.push(node);
    stack.push(node);
    currentLevel = node.children;
  };

  const pushNoMarkerRoot = (content: string, extraSpaces = 0, spacer = false) => {
    const node: BulletNode = { content, children: [], indent: 0, noMarker: true, extraSpaces, spacer };
    tree.push(node);
  };

  for (const raw of lines) {
    const trimmed = raw.trim();

    // blank line
    if (trimmed.length === 0) {
      if (!seenAnyBullet) {
        if (leadParts.length === 0 || leadParts[leadParts.length - 1] !== "") {
          leadParts.push("");
        }
      } else {
        // spacer between list items
        pushNoMarkerRoot("", 0, true);
      }
      continue;
    }

    // ---------- bullet lines (DO NOT treat "*" as a bullet) ----------
    // ordered: "1. " or "1) "
    let m = raw.match(/^(\s*)(\d+[.)])\s+(.*)$/);
    if (m) {
      seenAnyBullet = true;
      const indent = m[1].length;
      const rest = m[3] ?? "";
      pushBullet(indent, rest.trimEnd());
      continue;
    }

    // dash bullet: allow immediate typing after '-' (optional space)
    m = raw.match(/^(\s*)-(?:\s+|$)(.*)$/);
    if (m) {
      seenAnyBullet = true;
      const indent = m[1].length;
      const rest = m[2] ?? "";
      pushBullet(indent, rest.trimEnd());
      continue;
    }

    // • bullet: require a space after the dot
    m = raw.match(/^(\s*)•\s+(.*)$/);
    if (m) {
      seenAnyBullet = true;
      const indent = m[1].length;
      const rest = m[2] ?? "";
      pushBullet(indent, rest.trimEnd());
      continue;
    }

    // ---------- non-bullet lines ----------
    const leading = raw.match(/^(\s+)(.*)$/);
    if (leading) {
      const extra = leading[1].length;
      const content = leading[2] ?? "";
      pushNoMarkerRoot(content, extra, false);
    } else {
      pushNoMarkerRoot(raw, 0, false);
    }
  }

  const leadRaw = leadParts.join("\n");
  const lead = /\S/.test(leadRaw) ? leadRaw : null;

  return { lead, tree };
}

/** Recursive renderer */
function RenderBulletList({ nodes }: { nodes: BulletNode[] }) {
  if (!nodes || nodes.length === 0) return null;

  return (
    <ul
      className="note-bullets"
      style={{
        listStylePosition: "outside",
        whiteSpace: "pre-wrap",
        margin: 0,
        paddingLeft: 20,  // keep RP feel
        display: "block",
      }}
    >
      {nodes.map((node, i) => {
        // spacer blank line
        if (node.noMarker && node.spacer) {
          return (
            <li
              key={`sp-${i}`}
              style={{ listStyleType: "none", height: 18, padding: 0, margin: 0 }}
              aria-hidden
            />
          );
        }

        return (
          <li
            key={i}
            style={{
              whiteSpace: "inherit",
              listStyleType: node.noMarker ? "none" : undefined,
              display: "list-item",
              ...(node.noMarker && node.extraSpaces ? { marginLeft: `${node.extraSpaces}ch` } : null),
            }}
          >
            {parseInline(node.content)}
            {!node.noMarker && <RenderBulletList nodes={node.children} />}
          </li>
        );
      })}
    </ul>
  );
}

// --- MAIN ---

export default function NoteBody({ text }: { text: string }) {
  if (!text) return null;
  const norm = normalizeNoteText(text);
  const { lead, tree } = buildBulletTree(norm);

  return (
    <div className="note-body">
      {lead && <div className="note-body-text">{parseInline(lead)}</div>}
      <RenderBulletList nodes={tree} />
    </div>
  );
}
